import { Refresh, Watch } from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs, { type Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router-dom";
import { LargeContentBox } from "@/components/common";
import { PageHeader } from "@/components/common/PageHeader";
import { ScreenMobile } from "@/components/common/ScreenMobile";
import {
  FLUXHEALTH_RANGE_PRESETS,
  isValidFluxHealthRangeDays,
  type FluxHealthRangeDays,
  WearableDateRangeChips,
} from "@/pages/medicalcare/components/Wearable/WearableDateRangeChips";
import { WearablePeriodDateRow } from "@/pages/medicalcare/components/Wearable/WearablePeriodDateRow";
import {
  formatWearablePeriodLabel,
  resolveWearablePeriod,
} from "@/pages/medicalcare/components/Wearable/wearableMetricAggregation";
import { useSessionContext } from "@/contexts/SessionContext";
import { WearableEmptyState } from "./components/WearableEmptyState";
import {
  ObservationSection,
  SECTION_META,
} from "./components/ObservationSection";
import { PatientNoteSection } from "./components/PatientNoteSection";
import {
  PeriodChartStyleToggle,
  type PeriodChartStyle,
} from "./components/PeriodChartStyleToggle";
import {
  collectAwakeningRecords,
  getLatestPatientNoteText,
} from "./observationNotes";
import { PeriodTrendsView } from "./components/PeriodTrendsView";
import { useWearableObservationsData } from "./useWearableObservationsData";

interface WearableObservationsProps {
  open?: boolean;
  onClose?: () => void;
  /** Sem PageHeader/ScreenMobile (pai controla o shell). */
  embedded?: boolean;
  /**
   * CPF do paciente no atendimento. Se omitido, usa username da sessão
   * (fluxo do próprio paciente).
   */
  patientId?: string;
  /** Sem o card de passos FluxHealth / Samsung Health no estado vazio. */
  hideSetupInstructions?: boolean;
}

/**
 * Observações FluxHealth. Intervalos: 1 / 7 / 15 / 30 dias.
 * Em multi-dia: visão linha (padrão) ou barras — um único toggle abaixo do filtro.
 */
export function WearableObservations({
  open,
  onClose,
  embedded = false,
  patientId: patientIdProp,
  hideSetupInstructions = false,
}: WearableObservationsProps) {
  const navigate = useNavigate();
  const { user } = useSessionContext();
  const patientId = patientIdProp || user?.username;

  const today = useMemo(() => dayjs().startOf("day"), []);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(() =>
    dayjs().startOf("day")
  );
  const [rangeDays, setRangeDays] = useState<FluxHealthRangeDays>(1);
  const [chartStyle, setChartStyle] = useState<PeriodChartStyle>("line");

  const { periodStart, periodEnd } = useMemo(
    () => resolveWearablePeriod({ selectedDate, rangeDays, today }),
    [selectedDate, rangeDays, today]
  );
  const periodLabel = formatWearablePeriodLabel(
    periodStart,
    periodEnd,
    rangeDays
  );
  const isToday = periodEnd.isSame(today, "day");

  const {
    loading,
    filteredObservations,
    issuedGroups,
    selectedIssuedTime,
    setSelectedIssuedTime,
    buckets,
    deviceNameOf,
    refetch,
  } = useWearableObservationsData(
    patientId,
    periodStart,
    periodEnd,
    rangeDays
  );

  const handleClose = () => {
    if (onClose) {
      onClose();
      return;
    }
    navigate("/home");
  };

  const changeDay = (delta: number) => {
    const candidate = selectedDate.add(delta, "day");
    setSelectedDate(candidate.isAfter(today, "day") ? today : candidate);
  };

  const pickDate = (date: Dayjs) => {
    const next = date.isAfter(today, "day") ? today : date.startOf("day");
    setSelectedDate(next);
  };

  const openDayDetails = (dateKey: string) => {
    const date = dayjs(dateKey);
    if (!date.isValid()) return;
    pickDate(date);
    setRangeDays(1);
  };

  const sections = SECTION_META.map((meta) => ({
    ...meta,
    items: buckets[meta.id],
    deviceName: deviceNameOf(buckets[meta.id]),
  })).filter((section) => section.items.length > 0);

  // Lidos de todas as Observations do dia: a nota pode vir em qualquer envio
  const awakeningRecords = useMemo(
    () => collectAwakeningRecords(filteredObservations),
    [filteredObservations]
  );
  // Em "Todos": a última nota do dia. Em um horário: a nota daquele envio.
  const patientNoteText = useMemo(() => {
    const noteEntries =
      selectedIssuedTime === "all"
        ? filteredObservations
        : (issuedGroups.find((group) => group.time === selectedIssuedTime)
            ?.items ?? []);
    return getLatestPatientNoteText(noteEntries);
  }, [filteredObservations, issuedGroups, selectedIssuedTime]);

  /** Faixa verde só com período + data (sem toggle de gráfico — evita barra esmagada). */
  const dateHeader = (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1, sm: 1.5 },
        backgroundColor: "primary800.main",
        color: "white",
        borderRadius: 2,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        // No atendimento (flex column) sem isso a barra encolhe e some o conteúdo
        flexShrink: 0,
      }}
    >
      <Stack spacing={1.25} sx={{ width: "100%", minWidth: 0 }}>
        <Box
          sx={{
            width: "100%",
            minWidth: 0,
            overflowX: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <WearableDateRangeChips
            rangeDays={rangeDays}
            onChange={(days) => {
              if (isValidFluxHealthRangeDays(days)) setRangeDays(days);
            }}
            disabled={loading}
            tone="onPrimary"
            presets={FLUXHEALTH_RANGE_PRESETS}
          />
        </Box>

        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          justifyContent="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}
        >
          <Box sx={{ minWidth: 0, flex: "1 1 220px", maxWidth: 360 }}>
            <WearablePeriodDateRow
              periodLabel={periodLabel}
              periodEnd={periodEnd}
              today={today}
              loading={loading}
              onPrevDay={() => changeDay(-1)}
              onNextDay={() => changeDay(1)}
              onPickDate={pickDate}
            />
          </Box>
          <Button
            size="small"
            variant="outlined"
            disabled={loading || isToday}
            onClick={() => pickDate(today)}
            sx={{
              minWidth: 56,
              flexShrink: 0,
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.8125rem",
              color: "white",
              borderColor: (t) => alpha(t.palette.common.white, 0.4),
              bgcolor: (t) => alpha(t.palette.common.white, 0.08),
              "&:hover": {
                borderColor: (t) => alpha(t.palette.common.white, 0.65),
                bgcolor: (t) => alpha(t.palette.common.white, 0.16),
              },
              "&.Mui-disabled": {
                color: (t) => alpha(t.palette.common.white, 0.32),
                borderColor: (t) => alpha(t.palette.common.white, 0.14),
              },
            }}
          >
            Hoje
          </Button>
          <Tooltip title="Atualizar dados do período">
            <span>
              <IconButton
                size="small"
                onClick={refetch}
                disabled={loading || !patientId}
                aria-label="Atualizar observações do período"
                sx={{
                  color: "white",
                  border: "1px solid",
                  borderColor: (t) => alpha(t.palette.common.white, 0.4),
                  bgcolor: (t) => alpha(t.palette.common.white, 0.08),
                  "&:hover": {
                    borderColor: (t) => alpha(t.palette.common.white, 0.65),
                    bgcolor: (t) => alpha(t.palette.common.white, 0.16),
                  },
                  "&.Mui-disabled": {
                    color: (t) => alpha(t.palette.common.white, 0.32),
                    borderColor: (t) => alpha(t.palette.common.white, 0.14),
                  },
                }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );

  const issuedTimeChips =
    rangeDays === 1 && issuedGroups.length > 0 ? (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          overflowX: "auto",
          overflowY: "hidden",
          minWidth: 0,
          width: "100%",
          maxWidth: "100%",
          pb: 0.25,
          WebkitOverflowScrolling: "touch",
          overscrollBehaviorX: "contain",
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", flexShrink: 0 }}
        >
          Envio
        </Typography>
        <Button
          size="small"
          variant={selectedIssuedTime === "all" ? "contained" : "outlined"}
          color="primary"
          onClick={() => setSelectedIssuedTime("all")}
          sx={{ flexShrink: 0, borderRadius: 5, px: 2 }}
        >
          Todos
        </Button>
        {issuedGroups.map((group) => (
          <Button
            key={group.time}
            size="small"
            variant={
              selectedIssuedTime === group.time ? "contained" : "outlined"
            }
            color="primary"
            onClick={() => setSelectedIssuedTime(group.time)}
            sx={{ flexShrink: 0, borderRadius: 5, px: 2 }}
          >
            {group.time}
          </Button>
        ))}
      </Stack>
    ) : null;

  const periodCharts = (
    <PeriodTrendsView
      entries={filteredObservations}
      rangeDays={rangeDays}
      periodEndKey={periodEnd.format("YYYY-MM-DD")}
      chartStyle={chartStyle}
      chartStyleControl={
        <PeriodChartStyleToggle
          value={chartStyle}
          onChange={setChartStyle}
          disabled={loading}
        />
      }
      onSelectDay={openDayDetails}
    />
  );

  const body = loading ? (
    <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 240 }}>
      <CircularProgress />
    </Stack>
  ) : filteredObservations.length === 0 ? (
    <WearableEmptyState
      rangeDays={rangeDays}
      audience={embedded && !hideSetupInstructions ? "professional" : "patient"}
      hideSetupInstructions={hideSetupInstructions}
    />
  ) : rangeDays !== 1 ? (
    <Stack spacing={1.5} sx={{ width: "100%", minWidth: 0 }}>
      {periodCharts}
    </Stack>
  ) : (
    <Stack
      spacing={2}
      sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}
    >
      {issuedTimeChips}
      {sections.map((section) => (
        <ObservationSection
          key={section.id}
          title={section.title}
          Icon={section.Icon}
          deviceName={section.deviceName}
          items={section.items}
          awakeningRecords={
            section.id === "sleep" ? awakeningRecords : undefined
          }
        />
      ))}
      {/* Nota do paciente é a última seção da página */}
      {patientNoteText ? (
        <PatientNoteSection noteText={patientNoteText} />
      ) : null}
    </Stack>
  );

  const content = (
    <Stack
      spacing={2}
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "hidden",
        p: embedded ? { xs: 1, sm: 1.25 } : 0,
        // Embutido no atendimento (60vh + flex): barra fixa, corpo com scroll
        ...(embedded
          ? {
              flex: 1,
              minHeight: 0,
              height: "100%",
              overflow: "hidden",
            }
          : {}),
      }}
    >
      {dateHeader}
      <Box
        sx={
          embedded
            ? {
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                overflowY: "auto",
                overflowX: "hidden",
                WebkitOverflowScrolling: "touch",
              }
            : { width: "100%", minWidth: 0 }
        }
      >
        {body}
      </Box>
    </Stack>
  );

  if (embedded) {
    return content;
  }

  if (isMobile) {
    return (
      <ScreenMobile
        title="Dados Wearable"
        open={open ?? true}
        onClose={handleClose}
      >
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            overflowX: "hidden",
          }}
        >
          {content}
        </Box>
      </ScreenMobile>
    );
  }

  return (
    <LargeContentBox>
      <Stack
        spacing={2}
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          justifySelf: "stretch",
          overflowX: "hidden",
        }}
      >
        <PageHeader
          title="Dados Wearable"
          icon={Watch}
          description="Visualize métricas de saúde e fitness coletadas pelo FluxHealth"
     
        />
        {content}
      </Stack>
    </LargeContentBox>
  );
}
